import Vue from 'vue'
import Router from 'vue-router'
import Home from './Home.vue'
import Admin from './Admin.vue'
import JoinedRoom from "./components/JoinedRoom";
import Register from "./Register.vue";
import axios from './client';
import PageNotFound from './PageNotFound'
import AdminDashboard from './authentication/AdminDashboard'
import ForgotPassword from './authentication/ForgotPassword';
import AdminLogin from './authentication/AdminLogin';
import AdminSignUp from './authentication/AdminSignUp';
import PreCallCheck from './components/util/PreCallCheck';
import ThirdPartyDashboard from "./components/ThirdPartyDashboard";
import { showMessage } from './utils';
import store from "./store/index";
import FreeTrail from './FreeTrail.vue'

Vue.use(Router);

let router = new Router({
    mode: 'history',
    base: '/',
    routes: [
        {
            path: '/',
            name: 'Home',
            component: Home,
            props: true
        },
        {
            path: '/videofacilitator_secret_admin',
            name: 'Admin',
            component: Admin,
            props: true
        },
        {
            path: '/join/:roomName',
            name: 'Join',
            component: JoinedRoom,
            props: true
        },
        {
            path: '/thanksforusing',
            name: 'ThanksForUsing',
            beforeEnter() { location.href = process.env.VUE_APP_AFTERCALL_FEEDBACK_URL }
        },
        {
            path: '*',
            name: 'NotFound',
            component: PageNotFound,
            props: false
        },
        {
            path: '/register_now',
            name: 'Register',
            component: Register
        },
        {
            path: '/free_trial',
            name: 'FreeTrail',
            component: FreeTrail
        },
        {
            path: '/registrationsuccess',
            name: 'SuccessfulRegistration',
            beforeEnter() { location.href = process.env.VUE_APP_REGISTRATION_SUCCESS_URL }
        },
        {
            path: '/thanksforsignup',
            name: 'Signup',
            beforeEnter() { location.href = process.env.VUE_APP_THANKS_FOR_SIGNUP }
        },
        {
            path: '/verification_success',
            name: 'VerifiedEmail',
            beforeEnter() { location.href = process.env.VUE_APP_VERIFICATION_EMAIL_SUCCESS_URL }
        },
        {
            path: '/unsupported_browser',
            name: 'UnsupportedBrowser',
            beforeEnter() { location.href = process.env.VUE_APP_UNSUPPORTED_BROWSER_URL }
        },
        {
            path: '/admin/Dashboard',
            name: 'AdminDashboard',
            component: AdminDashboard,
            props: { dashboardVisible: true, isModal:false}
        },
        {
            path: '/forgot_password',
            name: 'ForgotPassword',
            component: ForgotPassword
        },
        {
            path: '/email_verification',
            name: 'VerifyEmail',
        },
        {
            path: '/admin_login',
            name: 'AdminLogin',
            component: AdminLogin
        },
        {
            path: '/sign_up',
            name: 'AdminSignUp',
            component: AdminSignUp
        },
        {
            path: '/register_room',
            name: 'RegisterRoom',
            beforeEnter() { location.href = process.env.VUE_APP_REGISTRATION_URL }
        },
        {
            path:'/precall-check',
            name:'PreCallCheck',
            component:PreCallCheck
        },
        {
            path:'/external',
            name:'ExternalApi',
            component: ThirdPartyDashboard
        }
    ]
});

router.beforeEach(async (to, from, next) => {
    if(to.name === 'AdminDashboard'){
        redirectToLoginScreenIfRequired();
    }else if(to.name === 'VerifyEmail'){
        await handleVerifyEmailRedirection();
        return;
    }else if (to.name === 'Join') {
        const shouldBreak = await handleJoinRouteRedirection();
        if(shouldBreak) return;
    }

    next();

    async function handleVerifyEmailRedirection() {
        try{
            await axios.get(to.query.verificationlink)
            next('/verification_success')
        } catch(err){
            showMessage('Something went wrong.', 'error')
        }
    }

    function redirectToLoginScreenIfRequired() {
        const loggedIn = store.state.user.profile.loggedIn;
        if (!loggedIn) {
            next('/admin_login');
        }
    }

    // @returns {Promise<boolean>} Return whether to break after this function execution
    async function handleJoinRouteRedirection() {
        try {
            if(!isDesktopSupportedBrowser()){
                next('/unsupported_browser');
                return true;
            }
            const urlID = getUrlIdByUrlParams();
            // fetch the current room data on joining the room
            const room = await getJoinedRoomData(urlID);
            if (!room) {
                redirectToLoginScreenIfNotInRoom();
                return true;
            }
            sessionStorage.setItem('session',room.sessionId);

            if (room.hasAccess ==false && (from.name === null || from.name !== 'Join')) {
                showMessage('Room Locked', 'error')
                next('/');
                return true;
            }

            if (room.hasAccess ==false && (from.name === 'Join')) {
                showMessage('Room Locked', 'error')
                next(false);
                return true;
            }

            const IsMaxUserLimitReached = room.parent && (
                // (room.lastN===process.env.VUE_APP_EXPANDED_VIDEO_MODE &&
                room.participants.length >=room.expandedRoomParticipantsLimit
                /*  )||
                (room.lastN!=process.env.VUE_APP_EXPANDED_VIDEO_MODE &&room.participants.length >=room.normalRoomParticipantsLimit) */
            );

            if(IsMaxUserLimitReached){
                showMessage('Max users limit reached', 'error');
                if(from.name === 'Join'){
                    next(false);
                }else{
                    next('/');
                }
                return true;
            }

            if(from.name==="Join")
                store.commit('rooms/changeRoom',room);

            const mainRoomName = store.state.mainRoomConfig.currentRoom.roomName || to.params.roomName;
            await getMainRoomConfig(mainRoomName);
            const mainRoom = await getMainRoomData(room);

            // It sets the current room and mainroom data after fetching the data to join the room
            store.commit('rooms/setCurrentRoom', room);
            store.commit('rooms/setMainRoom', mainRoom);
            const uri = room.parent? `/join/${mainRoom.name}?id=${urlID}`: `/join/${mainRoom.name}`;
            window.history.replaceState({}, 'vf', uri); // to maintain the history state properly

            return false;
        } catch (err) {
            if (err.isAxiosError && err.response?.status===404){
                alert('Room with the provided name does not exists.')
                redirectToLoginScreenIfNotInRoom();
            }else {
                showErrorAfter1Refresh();
            }

            return true;
        }
    }

    function showErrorAfter1Refresh() {
        if (sessionStorage.getItem("is_retry") === '1') {
            alert('Request failed. Please check your internet connection !');
            sessionStorage.removeItem("is_retry");
            redirectToLoginScreenIfNotInRoom();
            return;
        }
        showMessage('The page is being reloaded because of an error.', 'error')
        sessionStorage.setItem("is_retry", '1'); // mark retry
        window.location.reload(true);
    }

    async function getMainRoomData(room) {
        if(!room.parent) return room;
        const mainRoomResponse = await axios.get('/api/rooms/' + room.parent);
        return mainRoomResponse.data;
    }

    function redirectToLoginScreenIfNotInRoom() {
        if (from.name === null || from.name !== 'Join') {
            next('/')
            return
        }
        next(false)
        return
    }

    async function getJoinedRoomData(urlID) {
        const sessionId = sessionStorage.getItem('session');
        const response =  await axios.get(sessionId ? `/api/rooms/rooms/${urlID}?session=${sessionId}` : `/api/rooms/rooms/${urlID}`);
        return response.data;
    }

    function getUrlIdByUrlParams() {
        const roomIdOrMainRoom = (to.query && to.query.id) || to.params.roomName;
        const urlID = roomIdOrMainRoom.toLowerCase();
        if (from.name === null) {
            try {
                decodeURIComponent(urlID);
            }
            catch {
                return encodeURIComponent(urlID);
            }
        }
        return urlID;
    }
});


const getMainRoomConfig = async (roomName) => {
    await store.dispatch('mainRoomConfig/getMainRoomConfig', {roomName});
}

let isDesktopSupportedBrowser = ()=>{
    const { userAgent } = navigator;
    let isDesktop = !(userAgent.match(/Android/i) || userAgent.match(/iP(ad|hone|od)/i));
    if(isDesktop){
        const isChrome = userAgent.indexOf("Chrome") >=0;
        if(!isChrome){
            const FIREFOX = "Firefox";
            let isSafari = userAgent.match(/Safari/i);
            const firefoxVersionStartIndex = userAgent.indexOf(FIREFOX)+FIREFOX.length+ 1;
            if(isSafari)
            {
                return true;
            }
            else if(firefoxVersionStartIndex===FIREFOX.length){ // unable to find the firefox version
                return false;
            }else{
                let spaceIndex = userAgent.indexOf(' ',firefoxVersionStartIndex);
                if(spaceIndex=== (-1)){
                    spaceIndex = userAgent.length;
                }
                const firefoxVersion= userAgent.slice(firefoxVersionStartIndex,spaceIndex);
                return firefoxVersion>=80;
            }
        }
    }
    return true;
}

export default router;
